import { useContext, useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { Input } from 'atoms/Input';
import { type Audition } from 'hooks/u005/useGetAuditionHooks';
import { deleteApplicant } from 'hooks/u005/useDeleteApplicantHooks';
import { AppContext } from 'templates/AppContextProvider';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { PATHS } from 'commons/paths';
import { useNavigate } from 'react-router-dom';
import LoadingModal from 'organisms/LoadingModal';
import { RequiredLabel } from 'molecules/RequiredLabel';

const CLOSE_TYPE = {
  CANCEL: 'cancel',
  DELETE: 'delete',
  OUTSIDE: 'backdropClick',
  ESC: 'escapeKeyDown',
} as const;
type CloseType = (typeof CLOSE_TYPE)[keyof typeof CLOSE_TYPE];

type Props = {
  open: boolean;
  userId: string;
  auditionId: string;
  auditionName: string;
  reason: Audition['reason'];
  declineKbn: string;
  examinationNo: number;
  examinationKbn: string;
  examination_s_no: number;
  result: string;
  onClose: (value: boolean) => void;
};

export const DeleteApplicantDialog = (props: Props) => {
  const ctx = useContext(AppContext);
  const [errMsg, setErrMsg] = useState<boolean>(false);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [declineReasonInputProps, setDeclineReasonInputProps] = useState({
    title: '',
    required: false,
  });
  const [selectedDeclineReason, setSelectedDeclineReason] = useState<string | undefined>('');
  const [declineReason, setDeclineReason] = useState<string>('');

  const { t: tMessage } = useTranslationWithLang('message');
  const { t: tAuditionInfo } = useTranslationWithLang('auditionInfo');

  const replaceName = () => props.auditionName.replaceAll(/\\n/g, '');

  const reasonList = props.reason.filter((val) =>
    ctx.isGlobal ? val.svalue4 === '1' : val.svalue4 === '0'
  );

  const onChangeDeclineReasonSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sno = e.currentTarget.value;
    const reason = reasonList.find((reason) => reason.sno === sno);

    setSelectedDeclineReason(reason?.svalue);

    if (reason && reason.svalue2 !== '') {
      setDeclineReasonInputProps({
        title: reason.svalue2,
        required: reason.svalue3 === '1',
      });
    } else {
      setDeclineReason('');
      setDeclineReasonInputProps({
        title: '',
        required: false,
      });
    }
  };

  const handleClose = (_: object, reason?: CloseType) => {
    if (reason === CLOSE_TYPE.CANCEL) {
      setDeclineReason('');
      setSelectedDeclineReason('');
      setDeclineReasonInputProps({
        title: '',
        required: false,
      });
      props.onClose(false);
    } else if (reason === CLOSE_TYPE.DELETE) {
      setLoading(true);
      if (!selectedDeclineReason || selectedDeclineReason === '') return;
      const reason = `${selectedDeclineReason}\n${declineReasonInputProps.title} ${declineReason}`;
      deleteApplicant(
        props.userId,
        props.auditionId,
        reason,
        props.declineKbn,
        props.examinationNo,
        props.examinationKbn,
        props.examination_s_no,
        props.result
      )
        .then((res) => {
          setLoading(false);
          if (res.status === '00') {
            props.onClose(true);
            navigate(PATHS.MYPAGE);
          } else {
            console.error(res.msg);
            setErrMsg(true);
          }
        })
        .catch((err) => {
          console.error(err);
          setErrMsg(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <>
      {loading && <LoadingModal />}
      <Dialog open={props.open} onClose={handleClose} disablePortal sx={{ zIndex: 5 }}>
        <DialogTitle>{tAuditionInfo('TU005_016')}</DialogTitle>
        <DialogContent dividers>
          <Typography>{tAuditionInfo('TU005_017')}</Typography>
          <Typography fontWeight="bold">{replaceName()}</Typography>
          <div className="flex flex-col w-full mt-[5px]">
            <Typography className="text-[--c-error] whitespace-pre-wrap">
              {tAuditionInfo('TU005_051')}
            </Typography>

            <div className="flex flex-row items-baseline">
              <Typography className="!mt-[10px]">{tAuditionInfo('TU005_052')}</Typography>
              <RequiredLabel />
            </div>

            <select
              id="declineReason"
              className="text-[--c-textinput-text] bg-[--c-textinput-background] border-[--c-textinput-border] rounded-[8px] h-[40px] px-2 mt-[5px] border-[1px]"
              tabIndex={0}
              aria-required="true"
              onChange={(e) => onChangeDeclineReasonSelect(e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            >
              <option value="">{tAuditionInfo('TU005_053')}</option>
              {reasonList.map((reason) => {
                return <option value={reason.sno}>{reason.svalue}</option>;
              })}
            </select>
            {declineReasonInputProps.title !== '' && (
              <div className="mt-[10px]">
                <div className="flex flex-row">
                  <Typography className="!mr-[5px]">{declineReasonInputProps.title}</Typography>
                  {declineReasonInputProps.required && <RequiredLabel />}
                </div>
                <Input
                  type="text"
                  className="mt-[5px] rounded-lg"
                  onChange={(e) => setDeclineReason(e.currentTarget.value)}
                />
              </div>
            )}
          </div>
          {(() => {
            if (errMsg) {
              return (
                <div className="text-center pt-4">
                  <Typography className="text-[--c-error]">{tMessage('MU005F0057E')}</Typography>
                </div>
              );
            }
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(event) => handleClose(event, CLOSE_TYPE.CANCEL)}
            style={{ color: 'var(--c-textinput-label)' }}
            title={tAuditionInfo('TU005_018')}
          >
            {tAuditionInfo('TU005_018')}
          </Button>
          <Button
            onClick={(event) => handleClose(event, CLOSE_TYPE.DELETE)}
            autoFocus
            style={{ color: 'var(--c-error)' }}
            title={tAuditionInfo('TU005_019')}
            disabled={
              !selectedDeclineReason || (declineReasonInputProps.required && declineReason === '')
            }
          >
            {tAuditionInfo('TU005_019')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
